import React, { useEffect, useMemo } from 'react'
import { Col, Row, Space, Spin } from 'antd'
import { filter, groupBy, includes, isEmpty, map, values } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'
import { useQueryParams } from '../../../core/hooks'

import { HistoryLink } from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { EVENT_METADATA_TYPES, EVENT_TYPES } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useCleanupRedux,
  useDocumentTitle,
  useLanguagesQuery,
  useLatestEventByEntityTypesQuery,
} from '../../../common/hooks'

import { DICTIONARY_WORD_TYPES } from '../../constants'
import { renderDictionaryWordEvent } from '../../helpers'
import { cleanupFetchDictionaryWords } from '../../redux/actions'
import { DictionaryWordFilterSection, DictionaryWordTree } from '../sections'

const DictionaryWordsPage = () => {
  useDocumentTitle('Dictionary Words')
  useCleanupRedux(cleanupFetchDictionaryWords)
  const { dictionaryTypes = [] } = useQueryParams()

  const eventEntityTypes = useMemo(
    () => [EVENT_METADATA_TYPES.DICTIONARY_WORD],
    [],
  )

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    options: languageOptions,
    isLoading: isLoadingLanguages,
    error: languagesError,
  } = useLanguagesQuery()

  const dictionaryTypesToShow = useMemo(
    () =>
      isEmpty(dictionaryTypes)
        ? values(DICTIONARY_WORD_TYPES)
        : filter(values(DICTIONARY_WORD_TYPES), type =>
            includes(dictionaryTypes, type.value),
          ),
    [dictionaryTypes],
  )

  const languageOptionsGroupedByValue = useMemo(
    () => groupBy(languageOptions, 'value'),
    [languageOptions],
  )

  const eventTypes = useMemo(
    () => [
      EVENT_TYPES.UPDATE,
      EVENT_TYPES.CREATE,
      EVENT_TYPES.DELETE,
      EVENT_TYPES.CREATE_EXPORTED_DICTIONARY_WORD,
    ],
    [],
  )

  useEffect(() => {
    if (languagesError || fetchLatestEventError) {
      showErrorNotification({
        title: 'Dictionary Word error',
        description: languagesError || fetchLatestEventError,
      })
    }
  }, [fetchLatestEventError, languagesError])

  return isLoadingLanguages || isEmpty(languageOptions) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Dictionary Words</StyledPageTitle>
        <StyledPageActions>
          {isFetchLatestEventLoading ? (
            <Spin />
          ) : (
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                filterEventTypes
                renderEvent={renderDictionaryWordEvent}
                eventTypes={eventTypes}
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
          )}
        </StyledPageActions>
      </StyledPageHeader>
      <Row>
        <DictionaryWordFilterSection languageOptions={languageOptions} />
      </Row>
      <Row>
        {map(dictionaryTypesToShow, dictionaryType => (
          <Col span={8} key={dictionaryType.value}>
            <DictionaryWordTree
              refetchLastEvent={refetchLastEvent}
              type={dictionaryType}
              editable
              languageOptions={languageOptions}
              languageOptionsGroupedByValue={languageOptionsGroupedByValue}
            />
          </Col>
        ))}
      </Row>
    </>
  )
}

export default DictionaryWordsPage
